import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Theme state - default to dark mode
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  // Redirect authenticated users to main app
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      console.log('✅ Login - User already authenticated, redirecting to /main');
      navigate('/main', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Save theme preference and apply to document
  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(password);
      // Success - navigate to main dashboard
      navigate('/main');
    } catch (err) {
      setError(err.message || 'Incorrect password');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth to prevent flash
  if (authLoading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-white'} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // If authenticated, show nothing while redirecting
  if (isAuthenticated) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-white'} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'} flex items-center justify-center transition-colors duration-500`}>
      {/* Animated gradient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(139,92,246,0.4) 0%, rgba(59,130,246,0.2) 50%, transparent 100%)',
            left: '10%',
            top: '20%'
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(236,72,153,0.4) 0%, rgba(139,92,246,0.2) 50%, transparent 100%)',
            right: '10%',
            bottom: '20%'
          }}
        />
      </div>

      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 z-50 w-14 h-8 rounded-full transition-all duration-300 flex-shrink-0 ${
          isDark 
            ? 'bg-purple-500/30 border-2 border-purple-400/50' 
            : 'bg-gray-200 border-2 border-gray-300'
        } hover:scale-105`}
        aria-label="Toggle theme"
      >
        <div 
          className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
            isDark 
              ? 'translate-x-6 bg-gradient-to-br from-purple-400 to-pink-400 shadow-lg shadow-purple-400/50' 
              : 'translate-x-0 bg-white shadow-md'
          }`}
        >
          {isDark ? (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </button>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        <div className={`${isDark ? 'bg-gray-900/90 border-gray-700' : 'bg-white border-gray-200'} backdrop-blur-xl rounded-3xl p-10 border shadow-2xl`}>
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'bg-gradient-to-r from-white via-purple-200 to-white' : 'bg-gradient-to-r from-gray-900 via-purple-600 to-gray-900'} bg-clip-text text-transparent`}>
                ZuperBooks
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} font-medium`}>For DTC Brands</p>
            </div>
          </div>

          {/* Title */}
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2 text-center`}>
            Sign In
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-8 text-center`}>
            Enter your password to continue
          </p>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white focus:border-purple-500' 
                    : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-purple-500'
                } border focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all`}
                placeholder="Enter password"
                required
                disabled={loading}
                autoFocus
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-xl px-4 py-3">
                <p className="text-red-500 text-sm text-center font-semibold">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 hover:shadow-lg hover:shadow-purple-500/50 hover:scale-[1.02]'
              } text-white`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Info Text */}
          <div className={`mt-6 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <p className="font-semibold mb-2">Test Password: Zuperbook123</p>
            <p className="italic">Multi-tenant SaaS • All queries filter by tenant_id: tenant-123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
